import type { LiquidityTowerRow, SessionUser } from '@motoveo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { formatCents, formatDate } from '../lib/format';

/** Green when ≥ threshold, amber when ≥ half, red otherwise (the packet-completeness pill). */
function packetClass(score: number, threshold: number): string {
  if (score >= threshold) return 'conf-high';
  if (score >= threshold / 2) return 'conf-medium';
  return 'conf-low';
}

const REALISM_CLASS: Record<LiquidityTowerRow['price_realism'], string> = {
  ok: 'conf-high',
  marginal: 'conf-medium',
  above_band: 'conf-low',
  no_anchor: 'conf-medium',
  no_estimate: '',
};

/** The seller floor vs. the estimated dealer-bid band, compactly. */
function PriceCell({ row }: { row: LiquidityTowerRow }) {
  const anchor = row.seller_floor_cents ?? row.asking_price_cents;
  return (
    <div>
      <span className={`pill ${REALISM_CLASS[row.price_realism]}`}>{row.price_realism.replace('_', ' ')}</span>
      {row.valuation_verdict ? (
        <span
          className={`pill ${row.valuation_verdict === 'closable' ? 'conf-high' : row.valuation_verdict === 'unrealistic' ? 'conf-low' : ''}`}
          title="M11 appraisal verdict"
        >
          {' '}
          {row.valuation_verdict}
        </span>
      ) : null}
      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        floor {formatCents(row.seller_floor_cents)}
        {anchor != null && row.estimated_band
          ? ` vs ${formatCents(row.estimated_band.low_cents)}–${formatCents(row.estimated_band.high_cents)}`
          : ''}
      </div>
    </div>
  );
}

/**
 * The LIQUIDITY CONTROL TOWER — one readiness row per active submission: packet completeness, dealer
 * coverage, the launch-gate verdict, seller floor vs. estimated band, live bid/pass/no-response tallies,
 * top pass reasons, and close outcome. The one screen a founder uses to see which cars are ready to
 * send, blocked, or should be rejected (the direct defense against "marketplace theater").
 */
export function LiquidityTowerPage({ user }: { user: SessionUser }) {
  const [includeClosed, setIncludeClosed] = useState(false);
  const tower = useQuery({
    queryKey: ['cockpit', 'liquidity', includeClosed],
    queryFn: () => api.cockpit.liquidity(includeClosed),
  });

  return (
    <Layout user={user}>
      <div className="row-between">
        <h2>Liquidity control tower</h2>
        <label className="inline">
          <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} /> include closed
        </label>
      </div>

      {tower.data ? (
        <div className="toolbar">
          <span className="pill conf-high">{tower.data.summary.ready} ready</span>
          <span className="pill conf-low">{tower.data.summary.blocked} blocked</span>
          <span className="pill">{tower.data.summary.launched} launched</span>
          <span className="pill">{tower.data.summary.with_bids} with bids</span>
          <span className="pill conf-high">{tower.data.summary.closed_won} closed-won</span>
          <span className="muted">{tower.data.summary.total} cars</span>
        </div>
      ) : null}

      {tower.isLoading ? (
        <p className="muted">Loading readiness…</p>
      ) : tower.isError ? (
        <p className="error">{(tower.error as Error).message}</p>
      ) : tower.data && tower.data.rows.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Car</th>
                <th>Status</th>
                <th>Packet</th>
                <th>Network coverage</th>
                <th>Launch gate</th>
                <th>Price realism</th>
                <th>Seller risk</th>
                <th>Bids</th>
                <th>Top passes</th>
                <th>Deadline</th>
                <th>Close</th>
              </tr>
            </thead>
            <tbody>
              {tower.data.rows.map((r) => (
                <tr key={r.submission_id} className={r.named_buyer_count > 0 ? 'wedge-row' : ''}>
                  <td>
                    <Link to={`/submissions/${r.submission_id}`}>{r.public_ref}</Link>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {[r.model_year, r.model_designation].filter(Boolean).join(' ') || '—'}
                      {r.region_label ? ` · ${r.region_label}` : ''}
                    </div>
                  </td>
                  <td className="muted">{r.status}</td>
                  <td>
                    <span className={`pill ${packetClass(r.packet_score, 70)}`}>{r.packet_score}</span>
                    {r.packet_missing.length > 0 ? (
                      <div className="muted" style={{ fontSize: 11, marginTop: 4 }} title={r.packet_missing.join(', ')}>
                        missing {r.packet_missing.length}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <div>
                      <strong>{r.reachable_dealer_count}</strong> reachable franchise
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {r.matched_dealer_count} ISO match{r.matched_dealer_count === 1 ? '' : 'es'}
                      {r.named_buyer_count > 0 ? ` · ${r.named_buyer_count} buyer-in-hand` : ''}
                    </div>
                  </td>
                  <td>
                    {r.launch_ready ? (
                      <span className="pill conf-high">ready</span>
                    ) : (
                      <span className="pill conf-low">blocked</span>
                    )}
                    {r.launch_blockers.length > 0 ? (
                      <div className="error" style={{ fontSize: 11, marginTop: 4 }}>
                        {r.launch_blockers.join(', ')}
                      </div>
                    ) : null}
                    {r.launch_warnings.length > 0 ? (
                      <div className="warn" style={{ fontSize: 11, marginTop: 2 }}>
                        {r.launch_warnings.join(', ')}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <PriceCell row={r} />
                  </td>
                  <td>
                    {r.retail_expectation_risk === 'high' ? (
                      <span className="pill conf-low" title="anchored to retail comps">
                        retail-anchored
                      </span>
                    ) : r.retail_expectation_risk ? (
                      <span className="muted">{r.retail_expectation_risk}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                    <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                      dealer-direct: {r.accepts_dealer_direct == null ? '?' : r.accepts_dealer_direct ? 'yes' : 'no'}
                    </div>
                  </td>
                  <td>
                    <span title="asked / sent / bid / no-bid / ignored">
                      {r.asked_count}·{r.sent_count}·<strong>{r.bid_count}</strong>·{r.no_bid_count}·{r.ignored_count}
                    </span>
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {r.top_pass_reasons.length ? r.top_pass_reasons.map((p) => `${p.reason} (${p.count})`).join(', ') : '—'}
                  </td>
                  <td className="muted">{formatDate(r.bid_deadline)}</td>
                  <td>
                    {r.deal_status ? (
                      <span className={`pill ${r.deal_status === 'closed_won' ? 'conf-high' : r.deal_status === 'closed_lost' || r.deal_status === 'fell_through' ? 'conf-low' : ''}`}>
                        {r.close_outcome ?? r.deal_status}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">No active submissions. Intake a car to see its launch readiness here.</p>
      )}
      <p className="hint" style={{ marginTop: 12 }}>
        The launch gate blocks a blast unless seller consent, a complete packet, price realism, and minimum network coverage
        (reachable active franchise dealers) are satisfied. Blasts broadcast to the whole franchise network — ISO matches only
        prioritize/highlight. A founder may override with a recorded reason from the blast composer.
      </p>
    </Layout>
  );
}
